import type { ReactNode } from 'react';
import {
  Breadcrumbs,
  ContentHeader,
  FooterMobile,
  HeaderMobile,
  PlusButton,
  SideBar,
} from '../navigation';
import { Infos } from './Infos';
import { useNewPostModal } from '../post/post-form/NewPostModalContext';
import { useDefaultVisibility } from '../post/visibility';
import { useCurrentProfile } from './IdentityContext';
import { useRouter } from '../../contexts/router';
import { useOpenpeeps } from '../../contexts/openpeeps';

export interface RootLayoutProps {
  children?: ReactNode;
  /** Signed-in sidebar slots (desktop rail + mobile drawer). */
  sideBar?: {
    mainMenu?: () => ReactNode;
    profileMenu?: () => ReactNode;
  };
}

function MobileFooter() {
  const { openNewPost } = useNewPostModal();
  const visibility = useDefaultVisibility();
  const profile = useCurrentProfile();
  const { pathname } = useRouter();
  const { openpeepsApi } = useOpenpeeps();

  // Mirror Svelte FooterMobile: composing inside a group page pre-selects the
  // group so the new post lands in that group's context.
  const groupHandle = pathname.startsWith('/groups/@')
    ? (pathname.slice('/groups/@'.length).split('/')[0] ?? '')
    : '';
  const groupQuery = openpeepsApi.useGroupByHandle(groupHandle);
  const group = groupQuery.data;

  return (
    <FooterMobile
      onNewPost={
        profile
          ? () => {
              openNewPost(group ? { group } : { visibility });
            }
          : undefined
      }
    />
  );
}

/**
 * Translation of @openpeepshq/svelte/components/layout/Root.svelte. Provides the
 * top-level layout shell: on desktop, sidebar and main column sit in a row
 * inside the centered max-width band (`md:flex-row`); mobile header / footer,
 * plus button, and breadcrumbs / content header wrap `children`.
 */
export function RootLayout({ children, sideBar }: RootLayoutProps) {
  return (
    <div className="flex h-full w-full flex-col overflow-x-clip">
      <div className="min-w-0 flex-grow">
        <div className="flex min-h-full w-full flex-col overflow-y-auto overflow-x-clip">
          <div className="mx-auto h-1 w-full min-w-0 flex-grow md:max-w-[950px]">
            <div className="flex w-full min-w-0 flex-col md:flex-row">
              {/* Stretch the rail to the main column so it continues past
                  the viewport. Keep the nav viewport-tall and sticky.
                  overflow-x: clip (not hidden) so Firefox still honors sticky. */}
              <aside className="bg-background w-70 hidden shrink-0 border-r md:flex md:min-h-screen">
                <div className="w-full md:sticky md:top-0 md:h-screen">
                  <SideBar
                    mainMenu={sideBar?.mainMenu?.()}
                    profileMenu={sideBar?.profileMenu?.()}
                  />
                </div>
              </aside>
              <div className="bg-background flex min-h-screen min-w-0 flex-1 flex-col border-r">
                <HeaderMobile sideBar={sideBar} />
                <main className="flex min-w-0 flex-1 flex-col">
                  <ContentHeader />
                  <Breadcrumbs />
                  <Infos />
                  {children}
                </main>
                <PlusButton />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bottom-0 w-full flex-grow-0 md:hidden">
        <MobileFooter />
      </div>
    </div>
  );
}
